import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2,
  Wrench,
  AlertCircle,
  Search,
  X,
  Plus,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useRemontOrders } from './hooks/useRemont';
import { RemontSheet } from './components/RemontSheet';
import {
  serviceCenterRepairsApi,
  type RemontEntry,
} from '@/api/service-center-repairs';
import { LinkText } from '@/components/LinkText';

function StatusLine({ label, value, isYes }: { label: string; value: React.ReactNode; isYes?: boolean }) {
  return (
    <span className="flex gap-1">
      <span className="text-muted-foreground">{label}:</span>
      <span className={isYes ? '' : 'text-red-500'}>{value}</span>
    </span>
  );
}

function usePhotoUrl(photo: string | undefined) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!photo) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    async function fetchImage() {
      try {
        const nextUrl = await serviceCenterRepairsApi.getPhotoBlobUrl(photo!);
        if (!cancelled) {
          objectUrl = nextUrl;
          setUrl(objectUrl);
        }
      } catch {
        // Ignore errors
      }
    }

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [photo]);

  return photo ? url : null;
}

function RemontCard({
  entry,
  onClick,
}: {
  entry: RemontEntry;
  onClick: () => void;
}) {
  const photoUrl = usePhotoUrl(entry.photo);
  const costNum = parseFloat(entry.cost) || 0;
  const otvez = Number(entry.otvez) === 1;
  const zabral = Number(entry.zabral) === 1;
  const vistavleno = Number(entry.vistavlenoklientu) === 1;
  const oplacheno = Number(entry.oplacheno) === 1;
  const varhive = Number(entry.varhive) === 1;
  const hasAnswer = !!entry.answer?.trim();

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border overflow-hidden p-3 text-left transition-colors hover:bg-accent active:bg-accent ${
        entry.attentionLevel === 'RED'
          ? 'border-red-400 bg-red-50'
          : entry.attentionLevel === 'YELLOW'
            ? 'border-amber-400 bg-amber-50'
            : 'border-border bg-card'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate">{entry.name}</h3>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            @ {entry.company}
            {entry.whose && ` (${entry.whose})`}
            {entry.remont_place && ` → ${entry.remont_place}`}
          </p>
        </div>
        {photoUrl && (
          <img
            src={photoUrl}
            alt=""
            className="w-12 h-12 rounded object-cover flex-shrink-0"
          />
        )}
      </div>

      {/* Status like erp.js */}
      <div className="mt-2 text-xs space-y-0.5">
        {entry.comment && (
          <p className="text-muted-foreground">
            Жалоба: <LinkText>{entry.comment}</LinkText>
          </p>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          <StatusLine label="Отвёз" value={otvez ? 'Да' : 'Нет'} isYes={otvez} />
          <StatusLine label="Забрал" value={zabral ? 'Да' : 'Нет'} isYes={zabral} />
          <StatusLine label="Цена" value={costNum > 0 ? `${costNum} руб.` : 'Нет'} isYes={costNum > 0} />
          <StatusLine label="Ответ СЦ" value={hasAnswer ? <LinkText>{entry.answer!}</LinkText> : 'Нет'} isYes={hasAnswer} />
          <StatusLine label="Выставлено" value={vistavleno ? 'Да' : 'Нет'} isYes={vistavleno} />
          <StatusLine label="Оплата СЦ" value={oplacheno ? 'Да' : 'Нет'} isYes={oplacheno} />
          <StatusLine label="В архиве" value={varhive ? 'Да' : 'Нет'} isYes={varhive} />
        </div>
        {entry.attentionLevel !== 'NONE' && (
          <p className={entry.attentionLevel === 'RED' ? 'mt-1 text-red-700' : 'mt-1 text-amber-700'}>
            Без движения: {entry.inactivityDays} дн.
          </p>
        )}
        {entry.diagnostics.length > 0 && (
          <p className="mt-1 text-red-700">
            {entry.diagnostics.map((item) => item.message).join(' · ')}
          </p>
        )}
      </div>
    </button>
  );
}

export function RemontPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error } = useRemontOrders();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<Partial<RemontEntry> | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Handle prefilled client from URL
  const prefilledClient = searchParams.get('client');
  const prefilledClientId = searchParams.get('clientId');

  useEffect(() => {
    if ((prefilledClient || prefilledClientId) && data) {
      const parsedClientId = prefilledClientId ? Number(prefilledClientId) : null;
      // Open sheet with prefilled company name
      // URL prefill is an external navigation input that initializes the sheet.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedEntry({
        company: prefilledClient || '',
        clientId: parsedClientId && Number.isFinite(parsedClientId) ? parsedClientId : null,
      });
      setSheetOpen(true);
      // Clear URL param
      setSearchParams({}, { replace: true });
    }
  }, [prefilledClient, prefilledClientId, data, setSearchParams]);

  const filteredEntries = useMemo(() => {
    if (!data?.entries) return [];
    if (!searchQuery) return data.entries;

    const query = searchQuery.toLowerCase();
    return data.entries.filter(
      (entry) =>
        entry.name.toLowerCase().includes(query) ||
        entry.company.toLowerCase().includes(query) ||
        entry.whose?.toLowerCase().includes(query) ||
        entry.remont_place?.toLowerCase().includes(query)
    );
  }, [data, searchQuery]);

  const handleAddNew = () => {
    setSelectedEntry({});
    setSheetOpen(true);
  };

  const handleCardClick = (entry: RemontEntry) => {
    setSelectedEntry(entry);
    setSheetOpen(true);
  };

  const handleSheetClose = (open: boolean) => {
    setSheetOpen(open);
    if (!open) {
      setSelectedEntry(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Ремонт в СЦ"
        showBack
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            onClick={handleAddNew}
            className="h-9 w-9"
          >
            <Plus className="h-5 w-5" />
          </Button>
        }
      />

      <main className="p-4">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по устройству, клиенту, СЦ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Count */}
        {!isLoading && !error && data && (
          <p className="text-sm text-muted-foreground mb-3">
            {searchQuery
              ? `Найдено: ${filteredEntries.length} из ${data.entries.length}`
              : `Всего заявок: ${data.entries.length}`}
          </p>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-muted-foreground">Ошибка загрузки</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : 'Попробуйте позже'}
            </p>
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && data && (
          <div className="space-y-2">
            {filteredEntries.map((entry) => (
              <RemontCard
                key={entry.id}
                entry={entry}
                onClick={() => handleCardClick(entry)}
              />
            ))}

            {filteredEntries.length === 0 && searchQuery && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Search className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Ничего не найдено</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Попробуйте изменить запрос
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty state when no data */}
        {!isLoading && !error && data && data.entries.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Wrench className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Нет заявок на ремонт</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleAddNew}
            >
              <Plus className="w-4 h-4 mr-2" />
              Создать заявку
            </Button>
          </div>
        )}
      </main>

      {/* Remont Sheet */}
      <RemontSheet
        key={`${selectedEntry?.id ?? 'new'}-${sheetOpen}`}
        entry={selectedEntry}
        vendors={data?.vendors || []}
        open={sheetOpen}
        onOpenChange={handleSheetClose}
      />
    </div>
  );
}
